'use client';
import { useMemo } from 'react';
import { Alert } from '@//components/Alert';
import { OverflowGridItem } from '@//components/OverflowGrid';
import ProductCardContainer from '@//containers/ProductCardContainer';
import ProductCardSkeleton from '@//containers/ProductCardSkeleton';
import useVehicles from '@//data/search/vehicles/useVehicles';
import { useMarketMetadata } from '@//providers/MarketSettingsProvider';

const skeletonArray = new Array(3).fill(null);

interface StartpageLatestSearchesResultProps {
  searchQuery: string;
  testIdPrefix?: string;
}

const StartpageLatestSearchesResult = ({
  searchQuery,
  testIdPrefix = 'latest-search',
}: StartpageLatestSearchesResultProps) => {
  const marketMetadata = useMarketMetadata();

  const params = useMemo(() => {
    const baseParams = new URLSearchParams(searchQuery);
    baseParams.set('hits', '6');
    return baseParams;
  }, [searchQuery]);

  const vehiclesQuery = useVehicles(params);

  const latestVehicles = vehiclesQuery.data?.documentList?.documents || [];

  if (vehiclesQuery.isLoading)
    return (
      <>
        {skeletonArray.map((_, index) => (
          <OverflowGridItem key={index}>
            <div role="status" aria-live="polite" className="sr-only">
              Loading search results...
            </div>
            <ProductCardSkeleton key={index} />
          </OverflowGridItem>
        ))}
      </>
    );

  if (vehiclesQuery.isError)
    return <Alert id="alert-error">Det gick inte att hämta annonserna</Alert>;

  if (!latestVehicles.length)
    return (
      <Alert id="alert-no-vehicles">
        Det finns inga annonser att visa för den angivna sökningen just nu
      </Alert>
    );

  return (
    <>
      {latestVehicles.map((vehicle, index: number) => (
        <OverflowGridItem key={vehicle._id}>
          <ProductCardContainer
            item={vehicle}
            index={index}
            itemPage={marketMetadata.pageLinks.itemPage}
            currency={marketMetadata.currency}
            testId={`${testIdPrefix}-vehicle-${index}`}
            loading={index < 3 ? 'eager' : 'lazy'}
          />
        </OverflowGridItem>
      ))}
    </>
  );
};

export default StartpageLatestSearchesResult;
